import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useLazyQuery } from '@apollo/client';
import { Button, Typography } from '@guildeducationinc/recess';
import ApplicationPictogram from '@guildeducationinc/recess/pictograms/ApplicationPictogram';
import ApplicationReviewPictogram from '@guildeducationinc/recess/pictograms/ApplicationReviewPictogram';
import CheckMarkPictogram from '@guildeducationinc/recess/pictograms/CheckMarkPictogram';
import EligibilityPictogram from '@guildeducationinc/recess/pictograms/EligibilityPictogram';
import PiggyBankPictogram from '@guildeducationinc/recess/pictograms/PiggyBankPictogram';
import ReceiptPictogram from '@guildeducationinc/recess/pictograms/ReceiptPictogram';
import { DocumentSubmission } from '@src/components/Employee/PaymentRequests/DocumentSubmission/DocumentSubmission';
import { RequestNotification } from '@src/components/Employee/RequestNotification';
import { PAYMENT_REQUEST } from '@src/graphql/queries/paymentRequest';
import { PROGRAMS } from '@src/graphql/queries/program';
import { useAuth } from '@src/hooks/useAuth';
import { useMyBenefits } from '@src/hooks/useMyBenefits';
import { usePaymentRequests } from '@src/hooks/usePaymentRequests';
import { DeadlineDateType, PaymentMethod, PaymentRequestStatus, PreimbursementStatus, ProgramStatus, RuleType, } from '@src/types';
import { calculateIntervalDate, checkEligibilityLock, getDeadlineErrorMessage, } from '@src/utilities/paymentRequestHelper';
import classNames from 'classnames';
import { LoaderCircleIcon, useLocale } from 'pe-core-frontend';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './RequestSteps.module.scss';
export const RequestSteps = ({ className, showHeading, program, paymentRequest, isPaymentRequest, showCta, hideNotification, }) => {
    if (program.status === ProgramStatus.CLOSED || program.status === ProgramStatus.REJECTED) {
        return null;
    }
    const { t, formatToLocalDate } = useLocale();
    const { currentUser } = useAuth();
    const { myBenefits, getPolicy, loading: loadingBenefits } = useMyBenefits({ program });
    const showNotification = !hideNotification &&
        program.employeeId === currentUser?.employeeId &&
        ((!paymentRequest && program.status === ProgramStatus.INFO_REQUESTED) ||
            paymentRequest?.status === PaymentRequestStatus.INFO_REQUESTED ||
            paymentRequest?.status === PaymentRequestStatus.AWAITING_DOCUMENTS);
    const { paymentRequests, loading: loadingPaymentRequests } = usePaymentRequests({
        programId: program.id,
        useCurrentUser: true,
    });
    const hasRequests = paymentRequests?.length > 0;
    const approvalChainPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
    const approvalDeadline = approvalChainPolicy?.paymentRequestApproval?.deadline || {};
    const eligibilityLock = useMemo(() => {
        if (!program)
            return {
                locked: false,
                canSubmitExpenses: false,
                expensesRequired: false,
                preimbursement: false,
            };
        const eligibilityPolicy = getPolicy(RuleType.ELIGIBILITY, program);
        const approvalPolicy = getPolicy(RuleType.APPROVAL_CHAIN, program);
        if (!paymentRequest) {
            return {
                locked: eligibilityPolicy?.enableEligibilityLock || false,
                canSubmitExpenses: false,
                expensesRequired: false,
                preimbursement: eligibilityPolicy?.enablePreimbursement || false,
            };
        }
        return checkEligibilityLock(paymentRequest, program, eligibilityPolicy, approvalPolicy);
    }, [getPolicy, program, paymentRequest]);
    let hideNextSteps = useMemo(() => {
        return (paymentRequest?.status === PaymentRequestStatus.AWAITING_DOCUMENTS ||
            (paymentRequest?.paymentMethod === PaymentMethod.PREIMBURSEMENT &&
                paymentRequest?.preimbursementStatus === PreimbursementStatus.PENDING_PROOF));
    }, [paymentRequest, program]);
    const [getPrograms] = useLazyQuery(PROGRAMS, {
        variables: {
            spec: { employeeId: currentUser?.employeeId },
        },
    });
    const [getPaymentRequest] = useLazyQuery(PAYMENT_REQUEST, {
        variables: { id: paymentRequest?.id },
    });
    const handlReviewed = () => {
        if (paymentRequest) {
            getPaymentRequest();
        }
        else {
            getPrograms();
        }
    };
    const steps = useMemo(() => {
        const steps = [];
        if (!myBenefits?.approvalChains || myBenefits.approvalChains.length === 0)
            return [];
        const programApproval = myBenefits.approvalChains[0].programApproval;
        const approvalPolicyConfig = paymentRequest?.paymentMethod === PaymentMethod.PREIMBURSEMENT
            ? myBenefits.approvalChains[0].preimbursementApproval
            : myBenefits.approvalChains[0].paymentRequestApproval;
        const programApprovalDeadline = programApproval?.enableDeadline
            ? programApproval?.deadline
            : undefined;
        const paymentRequestApprovalDeadline = approvalPolicyConfig?.enableDeadline
            ? approvalPolicyConfig?.deadline
            : undefined;
        const documentDeadline = approvalPolicyConfig?.enableDocumentDeadline
            ? approvalPolicyConfig.documentDeadline
            : undefined;
        if (programApproval?.enableWorkflow && programApproval?.workflowId) {
            steps.push({
                title: t('Program under review'),
                icon: program.approvalDate ? CheckMarkPictogram : ApplicationReviewPictogram,
                description: t('Program will be reviewed by your employer'),
                active: program.status === ProgramStatus.IN_REVIEW && !program.approvalDate,
                complete: ![ProgramStatus.PENDING, ProgramStatus.IN_REVIEW].includes(program.status),
            });
        }
        if (paymentRequest && [PaymentRequestStatus.IN_REVIEW].includes(paymentRequest.status)) {
            steps.push({
                title: t('Under review'),
                icon: ApplicationReviewPictogram,
                description: t('Under review description'),
                active: true,
                complete: false,
            });
        }
        if (eligibilityLock.locked &&
            !eligibilityLock.expensesRequired &&
            paymentRequestApprovalDeadline?.minInterval?.days !== undefined &&
            documentDeadline?.minInterval?.days !== undefined &&
            documentDeadline?.maxInterval?.days !== undefined) {
            if (!isPaymentRequest && !paymentRequest && !hasRequests) {
                const numDays = paymentRequestApprovalDeadline.minInterval.days;
                // show this only for programs
                steps.push({
                    title: t('Confirm your eligibility for the current term or billing period'),
                    icon: EligibilityPictogram,
                    description: numDays === 0
                        ? t('You can confirm your benefit eligibility as early as when the term or billing period starts.')
                        : t('You can confirm your benefit eligibility as early as X days before the term or billing period starts.', {
                            values: {
                                days: numDays,
                            },
                        }),
                    active: true,
                });
            }
            else if (paymentRequest &&
                documentDeadline.minIntervalBasedOn &&
                documentDeadline.maxIntervalBasedOn) {
                const startDate = calculateIntervalDate(paymentRequest, documentDeadline.minIntervalBasedOn, documentDeadline.minInterval);
                const endDate = calculateIntervalDate(paymentRequest, documentDeadline.maxIntervalBasedOn, documentDeadline.maxInterval);
                const [description, descParams] = getDeadlineErrorMessage(formatToLocalDate, false, documentDeadline, startDate ?? null, endDate ?? null);
                steps.push({
                    title: t('Submit expenses for this term as early as X and no later than Y', {
                        values: {
                            startDate,
                            endDate,
                        },
                    }),
                    icon: EligibilityPictogram,
                    description: t(description, {
                        values: descParams,
                    }),
                    active: true,
                });
            }
            if (steps.length > 0) {
                return steps;
            }
        }
        steps.push({
            title: t('Make sure your program is covered'),
            icon: program.id ? CheckMarkPictogram : ApplicationPictogram,
            description: (programApprovalDeadline?.maxIntervalBasedOn?.toLowerCase() ===
                DeadlineDateType.START_DATE &&
                programApprovalDeadline?.maxInterval?.days &&
                t(programApprovalDeadline.maxInterval.backward
                    ? 'Apply up to X days before program start'
                    : 'Apply up to X days after program start', {
                    values: { days: programApprovalDeadline.maxInterval.days },
                })) ||
                undefined,
            active: !program.id,
            complete: !!program.id,
        });
        let requiresPaymentApproval = false;
        if (paymentRequestApprovalDeadline?.maxIntervalBasedOn &&
            paymentRequestApprovalDeadline?.maxInterval?.days) {
            const isTermStart = paymentRequestApprovalDeadline.maxIntervalBasedOn === DeadlineDateType.START_DATE;
            let descriptionKey = '';
            if (isTermStart) {
                descriptionKey = paymentRequestApprovalDeadline.maxInterval.backward
                    ? 'Apply up to X days before term start'
                    : 'Apply up to X days after term start';
            }
            else {
                descriptionKey = paymentRequestApprovalDeadline.maxInterval.backward
                    ? 'Apply up to X days before term end'
                    : 'Apply up to X days after term end';
            }
            steps.push({
                title: 'Submit reimbursement request',
                icon: paymentRequest ? CheckMarkPictogram : ReceiptPictogram,
                description: t(descriptionKey, {
                    values: { days: paymentRequestApprovalDeadline.maxInterval.days },
                }),
                active: isPaymentRequest || program.status === ProgramStatus.ACTIVE,
                complete: paymentRequest?.status && ![PaymentRequestStatus.PENDING].includes(paymentRequest.status),
            });
            requiresPaymentApproval = true;
        }
        let isPaymentApprovalComplete = false;
        if (requiresPaymentApproval) {
            isPaymentApprovalComplete =
                (paymentRequest &&
                    [
                        PaymentRequestStatus.ACTIVE,
                        PaymentRequestStatus.DOCUMENT_REVIEW,
                        PaymentRequestStatus.ON_HOLD,
                        PaymentRequestStatus.INFO_REQUESTED,
                        PaymentRequestStatus.READY_FOR_PAYMENT,
                        PaymentRequestStatus.CLOSED,
                        PaymentRequestStatus.APPROVED,
                        PaymentRequestStatus.REJECTED,
                    ].includes(paymentRequest.status)) ||
                    false;
            steps.push({
                title: t('Under review'),
                icon: isPaymentApprovalComplete ? CheckMarkPictogram : ApplicationReviewPictogram,
                description: t('Under review description'),
                active: paymentRequest?.status === PaymentRequestStatus.IN_REVIEW,
                complete: isPaymentApprovalComplete,
            });
        }
        let submitDescription = t('Submit your reimbursable expenses');
        if (approvalPolicyConfig?.documentDeadline?.maxIntervalBasedOn &&
            approvalPolicyConfig?.documentDeadline?.maxInterval?.days) {
            submitDescription = t(approvalPolicyConfig.documentDeadline.maxInterval.backward
                ? 'Submit expenses before term end'
                : 'Submit expenses after term end', {
                values: { days: approvalPolicyConfig.documentDeadline.maxInterval.days },
            });
        }
        const isPaymentRequestSubmitted = paymentRequest &&
            [
                PaymentRequestStatus.DOCUMENT_REVIEW,
                PaymentRequestStatus.ON_HOLD,
                PaymentRequestStatus.INFO_REQUESTED,
                PaymentRequestStatus.READY_FOR_PAYMENT,
                PaymentRequestStatus.REJECTED,
                PaymentRequestStatus.CLOSED,
                PaymentRequestStatus.APPROVED,
            ].includes(paymentRequest.status);
        steps.push({
            title: t('Submit expenses'),
            icon: isPaymentRequestSubmitted ? CheckMarkPictogram : ReceiptPictogram,
            description: submitDescription,
            active: program.status === ProgramStatus.ACTIVE &&
                (!requiresPaymentApproval || isPaymentApprovalComplete),
            complete: paymentRequest && (!requiresPaymentApproval || isPaymentRequestSubmitted),
        });
        const isPaymentRequestReviewComplete = paymentRequest &&
            [
                PaymentRequestStatus.READY_FOR_PAYMENT,
                PaymentRequestStatus.REJECTED,
                PaymentRequestStatus.CLOSED,
                PaymentRequestStatus.APPROVED,
            ].includes(paymentRequest.status);
        steps.push({
            title: t('Under review'),
            icon: isPaymentRequestReviewComplete ? CheckMarkPictogram : ApplicationReviewPictogram,
            description: t('Under review description'),
            active: paymentRequest &&
                [
                    PaymentRequestStatus.DOCUMENT_REVIEW,
                    PaymentRequestStatus.ON_HOLD,
                    PaymentRequestStatus.INFO_REQUESTED,
                ].includes(paymentRequest.status),
            complete: isPaymentRequestReviewComplete,
        });
        if (paymentRequest?.status !== PaymentRequestStatus.REJECTED) {
            steps.push({
                title: t('Get paid'),
                icon: PiggyBankPictogram,
                description: t('Get paid description'),
                active: isPaymentRequestReviewComplete,
                complete: paymentRequest &&
                    [
                        PaymentRequestStatus.READY_FOR_PAYMENT,
                        PaymentRequestStatus.CLOSED,
                        PaymentRequestStatus.APPROVED,
                    ].includes(paymentRequest.status),
            });
            if (paymentRequest?.status === PaymentRequestStatus.AWAITING_DOCUMENTS ||
                (paymentRequest?.paymentMethod === PaymentMethod.PREIMBURSEMENT &&
                    !paymentRequest.preimbursementStatus)) {
                steps.push({
                    title: t('Submit proof of payment and completion'),
                    icon: ApplicationPictogram,
                    description: t('Upload your proof of payment and confirmation of completion for the program or expense.'),
                    active: isPaymentRequestReviewComplete,
                    complete: paymentRequest &&
                        [
                            PaymentRequestStatus.READY_FOR_PAYMENT,
                            PaymentRequestStatus.CLOSED,
                            PaymentRequestStatus.APPROVED,
                        ].includes(paymentRequest.status),
                });
            }
        }
        return steps.filter(s => !s.complete); // Filter out completed steps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        myBenefits?.approvalChains,
        program.id,
        program.approvalDate,
        program.status,
        paymentRequest,
        isPaymentRequest,
        eligibilityLock,
        hasRequests,
    ]);
    const type = isPaymentRequest || !!paymentRequest ? 'payment_request' : 'program';
    const activity = {
        id: `${type}-${paymentRequest?.id || program.id}`,
        activityId: paymentRequest?.id || program.id,
        program,
        paymentRequest,
        type,
        createdAt: paymentRequest?.createdAt || program.createdAt,
        updatedAt: paymentRequest?.updatedAt || program.updatedAt,
    };
    const isComplete = [
        PaymentRequestStatus.READY_FOR_PAYMENT,
        PaymentRequestStatus.CLOSED,
        PaymentRequestStatus.APPROVED,
        PaymentRequestStatus.REJECTED,
    ].includes(paymentRequest?.status || PaymentRequestStatus.PENDING);
    if (loadingBenefits || loadingPaymentRequests) {
        return _jsx(LoaderCircleIcon, {});
    }
    if (steps.length === 0 || isComplete) {
        return null;
    }
    return (_jsxs("div", { className: className, children: [showHeading && !hideNextSteps && (_jsx(Typography, { variant: 'title2', className: styles.heading, children: t('Next step') })), _jsxs("div", { className: styles.container, children: [showNotification && (_jsx(RequestNotification, { activity: activity, onReviewed: handlReviewed, compact: !hideNextSteps })), !hideNextSteps && (_jsxs("div", { className: classNames(styles.steps, styles.compact), children: [steps.slice(0, 1).map(step => (_jsxs("div", { className: classNames(styles.step, step.complete ? styles.complete : styles.active //make step active for now since we are only showing the first step
                                // step.complete ? styles.complete : step.active && styles.active
                                // idx < steps.length - 1 && styles.line // Don't show line on last step
                                ), children: [_jsx("div", { className: classNames(styles.stepIcon, !step.active && styles.invertColors), children: _jsx(step.icon, {}) }), _jsxs("div", { className: styles.stepContent, children: [_jsx(Typography, { variant: 'label1', children: step.title }), step.description && _jsx(Typography, { variant: 'label2', children: step.description })] })] }, step.title))), showCta && (_jsxs("div", { children: [paymentRequest &&
                                        eligibilityLock.canSubmitExpenses &&
                                        paymentRequest.status === PaymentRequestStatus.ACTIVE && (_jsx(Button, { Component: Link, to: `/payments/${paymentRequest.id}/edit`, children: t('Submit expenses') })), !paymentRequest && program.status === ProgramStatus.ACTIVE && (_jsx("div", { children: _jsxs("div", { className: 'mt-2 flex flex-col gap-2', children: [eligibilityLock.locked && hasRequests && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: 'body1', weight: '700', children: t('Are you continuing with another term?') }), _jsx(Typography, { variant: 'body1', children: t((approvalDeadline.minInterval?.days ?? 0) === 0
                                                                ? 'Confirm term eligibility description_0_days'
                                                                : 'Confirm term eligibility description', {
                                                                values: { days: approvalDeadline.minInterval?.days ?? 0 },
                                                            }) })] })), _jsxs("div", { className: 'mt-4 flex gap-4', children: [hasRequests && (_jsx(Button, { Component: Link, to: `/payments/new/${program.id}/choose`, children: t('Submit expenses') })), eligibilityLock.locked && (_jsx(Button, { Component: Link, to: `/payments/new/${program.id}`, appearance: 'outline', children: t('Tell us about a new term') }))] })] }) }))] }))] })), activity.paymentRequest &&
                        activity.paymentRequest.status === PaymentRequestStatus.INFO_REQUESTED && (_jsx(DocumentSubmission, { paymentRequest: activity.paymentRequest, program: activity.paymentRequest.program }))] })] }));
};
